import styled from "@emotion/styled"
import { css, keyframes } from "@emotion/react"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* EGDE-318: each row is rendered as FOUR identical sets and the loop advances
   exactly one set (-25% → -50% of the 4-set track). Starting one set in and
   ending two sets in leaves a full spare set on BOTH sides of the visible
   window, so even a narrow logo set can never expose a track edge. Endpoints
   are one set apart over identical content, so the loop restart is seamless
   (cf. EGDE-437). EGDE-670: a "set" is now the row's HALF of the logo list, so
   the two rows have their own set widths — the percentages stay correct because
   they are relative to each track's own width. */
export const scrollLeft = keyframes`
  0% { transform: translateX(-25%); }
  100% { transform: translateX(-50%); }
`

export const scrollRight = keyframes`
  0% { transform: translateX(-50%); }
  100% { transform: translateX(-25%); }
`

/* EGDE-510 point 5 — the "Logos Component" of the spec in the ticket's
   "Bug Description" field: "First row enters from right to left. Second row
   enters from left to right. Behavior identical on desktop + mobile", plus
   Raz's 2026-07-29 correction that the TITLE must enter from the screen's edge
   rather than from inside the grid.

   Root only publishes the trigger and the direction sign. The three moving
   parts own their own travel: TitleBar sweeps in from the screen edge (see
   --title-gap) and the two MarqueeRow wrappers come from OPPOSITE edges.
   Earlier rounds put a single bottom-to-top rise on Root (rejected), then a
   single horizontal move on the whole marquee section (one direction for both
   rows, so also wrong).

   --edge carries the LTR mirror because stylis-plugin-rtl does not flip
   transforms (same contract as FormShell). Each translate is paired with an
   opacity fade so a displaced element is invisible at its furthest point and
   never leaves a visible gap at the opposite edge. */
export const Root = styled.div<{ $entered?: boolean }>`
  width: 100%;
  background-color: ${colors.primitives.neutral0};
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  /* The two halves travel sideways on their way in; clip so neither can add
     horizontal page overflow. clip (not hidden) keeps this from becoming a
     scroll container. */
  overflow-x: clip;

  --edge: 1;
  --entered: ${(p) => (p.$entered ? 1 : 0)};

  [dir="ltr"] & {
    --edge: -1;
  }

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[24]}px;
  }

  @media (prefers-reduced-motion: reduce) {
    --entered: 1;
  }
`

export const TitleBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${sizes.spacing[40]}px;

  /* EGDE-510 point 5 (Raz, 2026-07-29): "The text ... enters from inside the
     container when the image is stretched edge to edge. The text should enter
     from the right edge of the screen."
     A fixed offset cannot express that: the title rests inside Container's
     inline padding, and how far that is from the viewport edge changes with
     the breakpoint (120px at 1440, 16px on mobile). --title-gap is measured in
     index.tsx as the real distance from the title's resting inline-start edge
     to the screen's, so the sweep STARTS at the screen edge on every width.
     The 80px fallback only applies before JS measures. */
  opacity: var(--entered, 1);
  transform: translateX(
    calc(var(--edge) * var(--title-gap, 80px) * (1 - var(--entered, 1)))
  );
  transition:
    opacity 0.7s cubic-bezier(0.33, 1, 0.68, 1),
    transform 0.7s cubic-bezier(0.33, 1, 0.68, 1);

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: none;
  }
`

export const Title = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700;
  flex: 1;
  text-align: start;
`

export const PauseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: ${sizes.borderRadius.full}px;
  background-color: ${colors.primitives.neutral0};
  border: 1.25px solid ${colors.primitives.neutral200};
  backdrop-filter: blur(5px);
  color: ${colors.primitives.primary500};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  /* EGDE-496: the on-white play/pause disc gets the subtle circle-button
     hover — tinted disc with a darker ring. */
  &:hover {
    background-color: ${colors.primitives.neutral100};
    border-color: ${colors.primitives.neutral500};
  }
`

export const MarqueeSection = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* EGDE-318: zero out the intrinsic inline size. The layout <main> centers
     its children (align-items: center → fit-content width), so without this
     the max-content of the marquee track (4 logo sets wide) propagates up and
     stretches <main>/the whole document to the track width — thousands of px
     of invisible horizontal overflow (and at ~5000px+ Chromium's compositor
     stops painting the page at all). overflow:hidden clips visually but does
     NOT shrink intrinsic size; contain:inline-size does. */
  contain: inline-size;
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Marquee row wrapper — carries the per-row ENTRANCE.                  */
/*                                                                     */
/* EGDE-510 point 5: "First row enters from right to left. Second row   */
/* enters from left to right." The two rows must therefore travel in    */
/* OPPOSITE directions — an earlier pass moved the whole MarqueeSection */
/* as one unit, which is a single direction for both.                   */
/*                                                                     */
/* The entrance cannot live on MarqueeTrack: the rAF marquee driver     */
/* writes an inline transform on the tracks every frame and would       */
/* overwrite it. Hence this wrapper between section and track.          */
/* --edge mirrors the pair for LTR, matching how the marquee's own      */
/* travel directions already mirror.                                    */
/* ------------------------------------------------------------------ */
export const MarqueeRow = styled.div<{ $fromStart?: boolean }>`
  width: 100%;
  opacity: var(--entered, 1);
  transform: translateX(
    calc(
      ${(p) => (p.$fromStart ? "1" : "-1")} * var(--edge) * 160px *
        (1 - var(--entered, 1))
    )
  );
  transition:
    opacity 0.8s cubic-bezier(0.33, 1, 0.68, 1),
    transform 0.8s cubic-bezier(0.33, 1, 0.68, 1);

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: none;
  }
`

/* Rendered width of one logo card: the fixed logo box plus the card's padding
   and 1px border on both sides (see LogoCard / LogoImage below). Used to size
   the no-JS fallback animation to the row's actual set width. */
const CARD_WIDTH_MOBILE = 93 + 2 * sizes.spacing[16] + 2
const CARD_WIDTH_DESKTOP = 163 + 2 * sizes.spacing[96] + 2
/* The rAF driver's drift, in px/s (EGDE-502, measured off aurora.tech). */
const FALLBACK_SPEED = 7

/** Seconds for one set of `cards` to travel its own width at FALLBACK_SPEED. */
const setDuration = (cards: number, cardWidth: number) =>
  Math.max(1, Math.round((cards * cardWidth) / FALLBACK_SPEED))

export const MarqueeTrack = styled.div<{
  $paused: boolean
  $direction: "left" | "right"
  /** Cards in ONE of the track's four sets — drives the fallback duration. */
  $setCards: number
}>`
  display: flex;
  flex-shrink: 0;
  /* EGDE-437: the track must be exactly as wide as its (repeated) content —
     when it stretches to the section width instead, the percentage keyframes
     shift by a fraction of the viewport rather than whole logo sets, so the
     loop restarts with a visible jump. */
  width: max-content;

  /* EGDE-603: the marquee always runs — the EGDE-318 editor toggle was removed
     (a static logo wall is not an approved state). These keyframes are the no-JS
     fallback; the rAF driver in index.tsx takes over once it mounts.

     EGDE-502 (r3): the fallback durations track the driver's drift of 7 px/s
     (the measured aurora.tech reference — see index.tsx), so a visitor without
     JS sees the same unhurried strip rather than the old ~83px/s conveyor. CSS
     can only express that as time-per-set, so the duration is derived from the
     row's own set width — mobile and desktop differ only because a card is
     127px wide against 357px.

     EGDE-670: derived rather than hardcoded, because the rows no longer hold the
     same number of logos. The old fixed 145s/410s assumed an eight-logo set in
     both rows; a 6/5 split would have run the two rows at different speeds. */
  ${(p) => css`
    animation: ${p.$direction === "left" ? scrollLeft : scrollRight}
      ${setDuration(p.$setCards, CARD_WIDTH_MOBILE)}s linear infinite;
    animation-play-state: ${p.$paused ? "paused" : "running"};

    @media (min-width: ${breakpoints.lg}px) {
      animation-duration: ${setDuration(p.$setCards, CARD_WIDTH_DESKTOP)}s;
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `}
`

export const LogoCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.primitives.neutral200};
  flex-shrink: 0;
  padding: ${sizes.spacing[16]}px;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[48]}px ${sizes.spacing[96]}px;
  }
`

export const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`

export const LogoImage = styled.img<{
  $hideOnMobile?: boolean
  $hideOnDesktop?: boolean
}>`
  /* EGDE-399: give every logo an identical fixed box and letterbox inside it
     with object-fit:contain. Mobile previously used width:auto, so each logo's
     footprint varied by its intrinsic aspect ratio — an SVG with a different
     aspect ratio rendered noticeably larger/smaller than the raster logos.
     A fixed box (matching the desktop 163:56 ≈ 2.9 ratio at 32px tall) makes
     the SVG and the raster logos share the same dimensions. */
  object-fit: contain;
  width: 93px;
  height: 32px;

  @media (min-width: ${breakpoints.lg}px) {
    width: 163px;
    height: 56px;
  }

  /* EGDE-78: viewport-specific logo when a dedicated mobile logo is set. */
  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    css`
      display: none;

      @media (min-width: ${breakpoints.lg}px) {
        display: block;
      }
    `}

  ${({ $hideOnDesktop }) =>
    $hideOnDesktop &&
    css`
      display: block;

      @media (min-width: ${breakpoints.lg}px) {
        display: none;
      }
    `}
`
